import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Wallet,
    CheckCircle2,
    Clock,
    CircleDot,
    AlertCircle,
    Upload,
    FileText,
    Landmark,
    Zap,
    Mail,
    Search,
    Download,
    X,
    Users,
} from "lucide-react";

const STATUS_META = {
    requested: {
        label: "Requested",
        style: "bg-gold-500/10 text-gold-700 border-gold-200",
        icon: Clock,
    },
    processing: {
        label: "Processing",
        style: "bg-navy-50 text-navy-700 border-navy-100",
        icon: CircleDot,
    },
    paid: {
        label: "Paid",
        style: "bg-emerald-50 text-emerald-700 border-emerald-200",
        icon: CheckCircle2,
    },
    on_hold: {
        label: "On hold",
        style: "bg-red-50 text-red-700 border-red-200",
        icon: AlertCircle,
    },
};

const METHOD_ICON = {
    bank: Landmark,
    stripe_connect: Zap,
    paypal: Mail,
};

const TABS = [
    { key: "requested", label: "Requested" },
    { key: "processing", label: "Processing" },
    { key: "paid", label: "Paid" },
    { key: "on_hold", label: "On hold" },
    { key: "all", label: "All" },
];

function money(cents, currency) {
    if (cents == null) return "—";
    return `${(currency || "hkd").toUpperCase()} ${(cents / 100).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
}

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
            year: "numeric",
        });
    } catch {
        return iso;
    }
}

export default function AdminAffiliatePayouts() {
    const [rows, setRows] = useState([]);
    const [counts, setCounts] = useState({});
    const [tab, setTab] = useState("requested");
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    // Report filters
    const [fltQuery, setFltQuery] = useState("");
    const [fltMethod, setFltMethod] = useState("all");
    const [fltAffiliate, setFltAffiliate] = useState("all");
    const [fltFrom, setFltFrom] = useState("");
    const [fltTo, setFltTo] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            // For the report tab we want ALL statuses; the tab counter
            // still respects the selected filter tab.
            const url =
                tab === "all"
                    ? "/admin/affiliate-payouts"
                    : `/admin/affiliate-payouts?status=${tab}`;
            const { data } = await api.get(url);
            setRows(data.items || []);
            setCounts(data.counts || {});
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, [tab]);

    // Unique affiliate options for the dropdown
    const affiliateOptions = useMemo(() => {
        const map = new Map();
        rows.forEach((r) => {
            if (r.user_id) {
                map.set(r.user_id, r.affiliate_name || r.affiliate_email || r.user_id);
            }
        });
        return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
    }, [rows]);

    // Apply filters
    const filtered = useMemo(() => {
        const fromTs = fltFrom ? new Date(fltFrom + "T00:00:00").getTime() : null;
        const toTs = fltTo ? new Date(fltTo + "T23:59:59.999").getTime() : null;
        const q = fltQuery.trim().toLowerCase();
        return rows.filter((r) => {
            if (fltMethod !== "all" && (r.payout_method || "") !== fltMethod) return false;
            if (fltAffiliate !== "all" && r.user_id !== fltAffiliate) return false;
            const anchor = r.paid_at || r.period_end || r.created_at;
            const ts = anchor ? new Date(anchor).getTime() : 0;
            if (fromTs && ts < fromTs) return false;
            if (toTs && ts > toTs) return false;
            if (q) {
                const hay = [
                    r.affiliate_name,
                    r.affiliate_email,
                    r.period_label,
                    r.reference,
                    r.payout_target,
                    r.status,
                ]
                    .join(" ")
                    .toLowerCase();
                if (!hay.includes(q)) return false;
            }
            return true;
        });
    }, [rows, fltQuery, fltMethod, fltAffiliate, fltFrom, fltTo]);

    // Report aggregates (across filtered rows)
    const aggregates = useMemo(() => {
        const paid = filtered.filter((r) => r.status === "paid");
        const pending = filtered.filter((r) =>
            ["requested", "processing"].includes(r.status)
        );
        const onHold = filtered.filter((r) => r.status === "on_hold");
        const paidTotal = paid.reduce((s, r) => s + (r.amount || 0), 0);
        const pendingTotal = pending.reduce((s, r) => s + (r.amount || 0), 0);
        const onHoldTotal = onHold.reduce((s, r) => s + (r.amount || 0), 0);
        const membersJoined = filtered.reduce((s, r) => s + (r.conversions || 0), 0);
        const affiliateSet = new Set(filtered.map((r) => r.user_id).filter(Boolean));
        return {
            paidCount: paid.length,
            pendingCount: pending.length,
            onHoldCount: onHold.length,
            paidTotal,
            pendingTotal,
            onHoldTotal,
            membersJoined,
            affiliateCount: affiliateSet.size,
            currency: filtered[0]?.currency || "hkd",
        };
    }, [filtered]);

    const filtersActive =
        fltQuery ||
        fltMethod !== "all" ||
        fltAffiliate !== "all" ||
        fltFrom ||
        fltTo;

    const applyPreset = (v) => {
        const now = new Date();
        const iso = (d) => d.toISOString().slice(0, 10);
        if (v === "30d") {
            const from = new Date();
            from.setDate(now.getDate() - 30);
            setFltFrom(iso(from));
            setFltTo(iso(now));
        } else if (v === "month") {
            setFltFrom(iso(new Date(now.getFullYear(), now.getMonth(), 1)));
            setFltTo(iso(now));
        } else if (v === "ytd") {
            setFltFrom(iso(new Date(now.getFullYear(), 0, 1)));
            setFltTo(iso(now));
        } else if (v === "last-month") {
            const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            const end = new Date(now.getFullYear(), now.getMonth(), 0);
            setFltFrom(iso(start));
            setFltTo(iso(end));
        }
    };

    const clearFilters = () => {
        setFltQuery("");
        setFltMethod("all");
        setFltAffiliate("all");
        setFltFrom("");
        setFltTo("");
    };

    const exportCSV = () => {
        const rowsOut = [
            [
                "Affiliate",
                "Email",
                "Period",
                "Members joined",
                "Commission",
                "Paid",
                "Outstanding",
                "Currency",
                "Status",
                "Payout method",
                "Payout target",
                "Reference",
                "Paid on",
                "Requested at",
            ],
            ...filtered.map((r) => {
                const total = (r.amount || 0) / 100;
                const paid = r.status === "paid" ? total : 0;
                const outstanding = r.status === "paid" ? 0 : total;
                return [
                    r.affiliate_name || "",
                    r.affiliate_email || "",
                    r.period_label || `${r.period_start} → ${r.period_end}`,
                    r.conversions ?? 0,
                    total.toFixed(2),
                    paid.toFixed(2),
                    outstanding.toFixed(2),
                    (r.currency || "hkd").toUpperCase(),
                    (r.status || "").toUpperCase(),
                    r.payout_method || "",
                    r.payout_target || "",
                    r.reference || "",
                    r.paid_at || "",
                    r.requested_at || "",
                ];
            }),
        ];
        const csv = rowsOut
            .map((r) =>
                r
                    .map((c) => {
                        const s = String(c ?? "");
                        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
                    })
                    .join(",")
            )
            .join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `perx-affiliate-payouts-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const exportPDF = () => {
        const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
        const pageW = doc.internal.pageSize.getWidth();
        const pageH = doc.internal.pageSize.getHeight();

        // Header band
        doc.setFillColor(0, 28, 80);
        doc.rect(0, 0, pageW, 90, "F");
        doc.setTextColor(212, 175, 55);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.text("PERX GROUP", 40, 34);
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(20);
        doc.setFont("times", "italic");
        doc.text("Affiliate payouts statement", 40, 60);

        // Meta
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        const today = new Date().toLocaleDateString(undefined, {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
        doc.text(`Generated ${today}`, pageW - 40, 34, { align: "right" });
        const range =
            fltFrom || fltTo
                ? `${fltFrom || "Beginning"} → ${fltTo || "Today"}`
                : "All time";
        doc.text(`Period: ${range}`, pageW - 40, 50, { align: "right" });
        if (fltAffiliate !== "all") {
            const name =
                affiliateOptions.find((o) => o[0] === fltAffiliate)?.[1] ||
                fltAffiliate;
            doc.text(`Affiliate: ${name}`, pageW - 40, 66, { align: "right" });
        }

        // Summary strip (affiliate-specific KPIs — 3 tiles now that we
        // dropped the "Affiliates in view" cell per user feedback).
        doc.setTextColor(0, 28, 80);
        doc.setFillColor(247, 244, 237);
        doc.rect(40, 110, pageW - 80, 44, "F");
        doc.setFont("helvetica", "bold");
        const cells = [
            ["MEMBERS JOINED", `${aggregates.membersJoined}`],
            ["PAYMENTS MADE", money(aggregates.paidTotal, aggregates.currency)],
            ["PAYMENTS REMAINING", money(aggregates.pendingTotal, aggregates.currency)],
        ];
        const cellW = (pageW - 80) / cells.length;
        cells.forEach((c, i) => {
            const x = 40 + cellW * i + 12;
            doc.setFont("helvetica", "normal");
            doc.setFontSize(7);
            doc.setTextColor(120, 120, 130);
            doc.text(c[0], x, 128);
            doc.setFont("helvetica", "bold");
            doc.setFontSize(14);
            doc.setTextColor(0, 28, 80);
            doc.text(c[1], x, 146);
        });

        // Table
        const head = [[
            "Affiliate",
            "Period",
            "Members joined",
            "Commission",
            "Paid",
            "Outstanding",
            "Status",
            "Method",
            "Reference",
            "Paid on",
        ]];
        const body = filtered.map((r) => {
            const total = r.amount || 0;
            const paid = r.status === "paid" ? total : 0;
            const outstanding = r.status === "paid" ? 0 : total;
            return [
                `${r.affiliate_name || ""}\n${r.affiliate_email || ""}`,
                r.period_label || `${r.period_start || ""} → ${r.period_end || ""}`,
                r.conversions ?? 0,
                money(total, r.currency),
                money(paid, r.currency),
                money(outstanding, r.currency),
                (r.status || "").toUpperCase(),
                (r.payout_method || "").replace("_", " "),
                r.reference || "—",
                r.paid_at
                    ? new Date(r.paid_at).toLocaleDateString()
                    : "—",
            ];
        });
        autoTable(doc, {
            head,
            body,
            startY: 170,
            margin: { left: 40, right: 40 },
            styles: { fontSize: 8, cellPadding: 6, textColor: [0, 28, 80] },
            headStyles: {
                fillColor: [0, 28, 80],
                textColor: [255, 255, 255],
                fontStyle: "bold",
                fontSize: 8,
            },
            alternateRowStyles: { fillColor: [252, 250, 245] },
            didParseCell: (data) => {
                if (data.section === "body") {
                    // Paid column — emerald when non-zero.
                    if (data.column.index === 4) {
                        const raw = String(data.cell.raw || "");
                        if (raw && !raw.endsWith("0.00")) {
                            data.cell.styles.textColor = [22, 130, 96];
                        } else {
                            data.cell.styles.textColor = [140, 140, 150];
                        }
                    }
                    // Outstanding column — gold/red when non-zero.
                    if (data.column.index === 5) {
                        const raw = String(data.cell.raw || "");
                        if (raw && !raw.endsWith("0.00")) {
                            data.cell.styles.textColor = [180, 130, 30];
                        } else {
                            data.cell.styles.textColor = [140, 140, 150];
                        }
                    }
                    // Status column
                    if (data.column.index === 6) {
                        const v = String(data.cell.raw || "");
                        if (v === "PAID") data.cell.styles.textColor = [22, 130, 96];
                        else if (v === "ON_HOLD" || v === "FAILED")
                            data.cell.styles.textColor = [200, 40, 40];
                        else if (v === "REQUESTED" || v === "PROCESSING")
                            data.cell.styles.textColor = [180, 130, 30];
                    }
                }
            },
            didDrawPage: () => {
                doc.setFont("helvetica", "normal");
                doc.setFontSize(8);
                doc.setTextColor(140, 140, 150);
                doc.text(
                    `Perx Group · affiliate payout statement · page ${doc.internal.getCurrentPageInfo().pageNumber}`,
                    pageW / 2,
                    pageH - 20,
                    { align: "center" }
                );
            },
        });

        const tag =
            fltAffiliate !== "all"
                ? `-${(affiliateOptions.find((o) => o[0] === fltAffiliate)?.[1] || "affiliate").replace(/\s+/g, "-").toLowerCase()}`
                : "";
        doc.save(`perx-affiliate-statement${tag}-${new Date().toISOString().slice(0, 10)}.pdf`);
    };

    return (
        <>
            <PageHeader
                eyebrow="Admin"
                title="Affiliate payouts"
                description="Review commission requests, upload payment receipts, and mark payouts paid. The affiliate ledger auto-refreshes as you act."
                testid="admin-aff-payouts-header"
            />

            <div className="px-6 lg:px-12 py-8 space-y-6">
                <div className="flex gap-1.5 flex-wrap" data-testid="aff-payout-tabs">
                    {TABS.map((t) => (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            data-testid={`aff-payout-tab-${t.key}`}
                            className={`px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-widest transition ${
                                tab === t.key
                                    ? "bg-navy-700 text-cream"
                                    : "bg-white border border-navy-100 text-navy-500 hover:bg-cream"
                            }`}
                        >
                            {t.label}{" "}
                            <span className="ml-1 opacity-70">
                                {counts[t.key] ?? 0}
                            </span>
                        </button>
                    ))}
                </div>

                {/* Affiliate-specific KPI strip */}
                <div className="grid sm:grid-cols-4 gap-4" data-testid="aff-payout-kpis">
                    <ReportKpi
                        icon={Users}
                        label="Affiliates in view"
                        value={aggregates.affiliateCount}
                        blurb="Unique creators across the current filter"
                    />
                    <ReportKpi
                        icon={Users}
                        label="Members joined"
                        value={aggregates.membersJoined}
                        blurb="Converted secondary members driven by these affiliates"
                    />
                    <ReportKpi
                        icon={CheckCircle2}
                        label="Payments made"
                        value={money(aggregates.paidTotal, aggregates.currency)}
                        blurb={`${aggregates.paidCount} paid payouts`}
                        accent="emerald"
                    />
                    <ReportKpi
                        icon={Clock}
                        label="Payments remaining"
                        value={money(aggregates.pendingTotal, aggregates.currency)}
                        blurb={`${aggregates.pendingCount} requested/processing${
                            aggregates.onHoldCount
                                ? ` · ${aggregates.onHoldCount} on hold`
                                : ""
                        }`}
                        accent="gold"
                    />
                </div>

                {/* Filter + export panel */}
                <div
                    data-testid="aff-payout-filters"
                    className="rounded-2xl bg-white border border-navy-100/70 shadow-soft p-5"
                >
                    <div className="flex flex-wrap items-end gap-4">
                        <div className="flex-1 min-w-[220px]">
                            <Label className="text-navy-700 text-[10px] uppercase tracking-widest">
                                Search
                            </Label>
                            <div className="relative mt-1.5">
                                <Search
                                    size={15}
                                    className="absolute left-3 top-1/2 -translate-y-1/2 text-navy-500"
                                />
                                <Input
                                    data-testid="aff-payout-search"
                                    value={fltQuery}
                                    onChange={(e) => setFltQuery(e.target.value)}
                                    placeholder="Affiliate, email, reference, period…"
                                    className="pl-9 bg-white border-navy-100 h-10"
                                />
                            </div>
                        </div>
                        <div>
                            <Label className="text-navy-700 text-[10px] uppercase tracking-widest">
                                Affiliate
                            </Label>
                            <Select value={fltAffiliate} onValueChange={setFltAffiliate}>
                                <SelectTrigger
                                    data-testid="aff-payout-flt-affiliate"
                                    className="mt-1.5 bg-white border-navy-100 h-10 w-52"
                                >
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-white">
                                    <SelectItem value="all">All affiliates</SelectItem>
                                    {affiliateOptions.map(([id, name]) => (
                                        <SelectItem key={id} value={id}>
                                            {name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-navy-700 text-[10px] uppercase tracking-widest">
                                Method
                            </Label>
                            <Select value={fltMethod} onValueChange={setFltMethod}>
                                <SelectTrigger
                                    data-testid="aff-payout-flt-method"
                                    className="mt-1.5 bg-white border-navy-100 h-10 w-40"
                                >
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-white">
                                    <SelectItem value="all">All</SelectItem>
                                    <SelectItem value="bank">Bank</SelectItem>
                                    <SelectItem value="stripe_connect">Stripe Connect</SelectItem>
                                    <SelectItem value="paypal">PayPal</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-navy-700 text-[10px] uppercase tracking-widest">
                                From
                            </Label>
                            <Input
                                type="date"
                                value={fltFrom}
                                onChange={(e) => setFltFrom(e.target.value)}
                                max={fltTo || undefined}
                                data-testid="aff-payout-flt-from"
                                className="mt-1.5 bg-white border-navy-100 h-10 w-44"
                            />
                        </div>
                        <div>
                            <Label className="text-navy-700 text-[10px] uppercase tracking-widest">
                                To
                            </Label>
                            <Input
                                type="date"
                                value={fltTo}
                                onChange={(e) => setFltTo(e.target.value)}
                                min={fltFrom || undefined}
                                data-testid="aff-payout-flt-to"
                                className="mt-1.5 bg-white border-navy-100 h-10 w-44"
                            />
                        </div>
                    </div>

                    <div className="mt-4 flex flex-wrap items-center gap-2">
                        {[
                            { v: "last-month", label: "Last month" },
                            { v: "month", label: "This month" },
                            { v: "30d", label: "Last 30 days" },
                            { v: "ytd", label: "Year to date" },
                        ].map((p) => (
                            <Button
                                key={p.v}
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => applyPreset(p.v)}
                                data-testid={`aff-payout-preset-${p.v}`}
                                className="rounded-full border-navy-100 text-navy-700 hover:bg-navy-50 h-9"
                            >
                                {p.label}
                            </Button>
                        ))}
                        {filtersActive && (
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={clearFilters}
                                data-testid="aff-payout-clear"
                                className="text-navy-500 h-9"
                            >
                                <X size={14} className="mr-1" /> Clear
                            </Button>
                        )}
                    </div>

                    <div className="mt-4 pt-4 border-t border-navy-100/70 flex items-center justify-between gap-4 flex-wrap">
                        <div className="text-sm text-navy-500">
                            Showing{" "}
                            <span className="text-navy-700 font-semibold">
                                {filtered.length}
                            </span>{" "}
                            of {rows.length}
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={exportCSV}
                                disabled={filtered.length === 0}
                                data-testid="aff-payout-export-csv"
                                className="rounded-full border-navy-100 text-navy-700 h-9"
                            >
                                <Download size={14} className="mr-1.5" /> Export CSV
                            </Button>
                            <Button
                                type="button"
                                size="sm"
                                onClick={exportPDF}
                                disabled={filtered.length === 0}
                                data-testid="aff-payout-export-pdf"
                                className="rounded-full bg-navy-700 hover:bg-navy-800 text-white h-9"
                            >
                                <Download size={14} className="mr-1.5" /> Statement PDF
                            </Button>
                        </div>
                    </div>
                </div>

                <div
                    data-testid="aff-payout-queue"
                    className="rounded-2xl bg-white border border-navy-100/70 overflow-hidden"
                >
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-cream">
                                <TableHead>Affiliate</TableHead>
                                <TableHead>Period</TableHead>
                                <TableHead className="text-center">Members joined</TableHead>
                                <TableHead className="text-right">Commission</TableHead>
                                <TableHead className="text-right">Paid</TableHead>
                                <TableHead className="text-right">Outstanding</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Receipt</TableHead>
                                <TableHead className="text-right">Action</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading && (
                                <TableRow>
                                    <TableCell colSpan={9} className="text-center py-10 text-navy-500">
                                        Loading queue…
                                    </TableCell>
                                </TableRow>
                            )}
                            {!loading && filtered.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={9} className="text-center py-14 text-navy-400">
                                        <Wallet size={26} className="mx-auto mb-2 text-navy-300" />
                                        {rows.length === 0
                                            ? "No affiliate payouts in this bucket."
                                            : "No rows match your filters."}
                                    </TableCell>
                                </TableRow>
                            )}
                            {filtered.map((r) => {
                                const meta = STATUS_META[r.status] || STATUS_META.requested;
                                const Icon = meta.icon;
                                const MIcon = METHOD_ICON[r.payout_method] || Landmark;
                                const totalC = r.amount || 0;
                                const paidC = r.status === "paid" ? totalC : 0;
                                const outstandingC = r.status === "paid" ? 0 : totalC;
                                return (
                                    <TableRow
                                        key={r.id}
                                        data-testid={`aff-payout-row-${r.id}`}
                                        className="cursor-pointer hover:bg-cream/60"
                                        onClick={() => setSelected(r)}
                                    >
                                        <TableCell>
                                            <div className="font-medium text-navy-700">
                                                {r.affiliate_name || "—"}
                                            </div>
                                            <div className="text-[11px] text-navy-500 flex items-center gap-1 mt-0.5">
                                                <MIcon size={11} className="text-navy-400" />
                                                {r.payout_target || (
                                                    <span className="text-red-600">Missing payout</span>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-navy-500 text-sm">
                                            {r.period_label || fmtDate(r.period_start)}
                                        </TableCell>
                                        <TableCell className="text-navy-700 font-semibold text-center">
                                            {r.conversions ?? 0}
                                        </TableCell>
                                        <TableCell className="text-navy-700 font-semibold text-right font-mono">
                                            {money(totalC, r.currency)}
                                        </TableCell>
                                        <TableCell
                                            className={`text-right font-mono ${
                                                paidC > 0 ? "text-emerald-700 font-semibold" : "text-navy-300"
                                            }`}
                                        >
                                            {money(paidC, r.currency)}
                                        </TableCell>
                                        <TableCell
                                            className={`text-right font-mono ${
                                                outstandingC > 0 ? "text-gold-700 font-semibold" : "text-navy-300"
                                            }`}
                                        >
                                            {money(outstandingC, r.currency)}
                                        </TableCell>
                                        <TableCell>
                                            <Badge
                                                className={`${meta.style} border uppercase tracking-widest text-[10px] gap-1`}
                                            >
                                                <Icon size={11} />
                                                {meta.label}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            {r.receipt_url ? (
                                                <a
                                                    href={r.receipt_url}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="inline-flex items-center gap-1 text-navy-700 text-xs underline underline-offset-2"
                                                >
                                                    <FileText size={12} />
                                                    View
                                                </a>
                                            ) : (
                                                <span className="text-navy-300 text-xs">—</span>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="rounded-full h-8 border-navy-200 text-xs"
                                                data-testid={`aff-payout-open-${r.id}`}
                                            >
                                                Open
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </div>
            </div>

            <PayoutActionDialog
                row={selected}
                onClose={() => setSelected(null)}
                onSaved={async () => {
                    await load();
                    setSelected(null);
                }}
            />
        </>
    );
}

function PayoutActionDialog({ row, onClose, onSaved }) {
    const [notes, setNotes] = useState("");
    const [reference, setReference] = useState("");
    const [busy, setBusy] = useState(null);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (!row) return;
        setNotes(row.admin_notes || "");
        setReference(row.reference || "");
    }, [row]);

    const persist = async (nextStatus) => {
        setBusy(nextStatus);
        try {
            await api.patch(`/admin/affiliate-payouts/${row.id}`, {
                status: nextStatus,
                admin_notes: notes,
                reference,
            });
            toast.success(
                nextStatus === "paid"
                    ? "Marked paid — affiliate ledger updated"
                    : nextStatus === "on_hold"
                    ? "Placed on hold"
                    : "Status updated"
            );
            await onSaved();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(null);
        }
    };

    const uploadReceipt = async (file) => {
        setUploading(true);
        try {
            const form = new FormData();
            form.append("file", file);
            const { data } = await api.post(
                `/admin/affiliate-payouts/${row.id}/receipt`,
                form,
                { headers: { "Content-Type": "multipart/form-data" } }
            );
            toast.success(`Receipt uploaded (${file.name})`);
            // Optimistically update the local row so the "View" link works
            // without needing a full reload.
            row.receipt_url = data.receipt_url;
            row.receipt_filename = file.name;
            await onSaved();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const summary = useMemo(() => {
        if (!row) return [];
        const rows = [
            ["Affiliate", row.affiliate_name || "—"],
            ["Email", row.affiliate_email],
            [
                "Period",
                row.period_label ||
                    `${fmtDate(row.period_start)} – ${fmtDate(row.period_end)}`,
            ],
            ["Conversions", row.conversions ?? 0],
            ["Amount", money(row.amount, row.currency)],
            [
                "Payout method",
                row.payout_method
                    ? row.payout_method.replace("_", " ")
                    : "Not set",
            ],
            ["Payout target", row.payout_target || "Not set"],
            [
                "Requested at",
                row.requested_at ? new Date(row.requested_at).toLocaleString() : "—",
            ],
            ["Paid at", row.paid_at ? new Date(row.paid_at).toLocaleString() : "—"],
        ];
        return rows;
    }, [row]);

    if (!row) return null;

    return (
        <Dialog open={!!row} onOpenChange={(v) => !v && onClose()}>
            <DialogContent
                data-testid="aff-payout-dialog"
                className="max-w-2xl bg-white max-h-[92vh] overflow-y-auto"
            >
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl text-navy-700 flex items-center gap-2">
                        <Wallet size={18} className="text-gold-600" />
                        Payout — {row.affiliate_name || row.affiliate_email}
                    </DialogTitle>
                    <DialogDescription>
                        Confirm the destination, attach the payment receipt, then mark
                        the payout paid. The affiliate&rsquo;s ledger refreshes
                        automatically.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-5">
                    <div className="rounded-2xl bg-cream border border-navy-100 p-4 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                        {summary.map(([k, v]) => (
                            <div
                                key={k}
                                className="flex justify-between gap-4 py-1 border-b last:border-b-0 border-navy-100/40"
                            >
                                <span className="text-navy-400 uppercase tracking-widest text-[10px]">
                                    {k}
                                </span>
                                <span className="text-navy-700 text-right break-words">
                                    {v}
                                </span>
                            </div>
                        ))}
                    </div>

                    <div>
                        <div className="text-[11px] uppercase tracking-widest text-gold-600 mb-2">
                            Payment reference
                        </div>
                        <Input
                            data-testid="aff-payout-reference"
                            value={reference}
                            onChange={(e) => setReference(e.target.value)}
                            placeholder="Bank / Stripe / PayPal transaction reference"
                            className="h-11 bg-white border-navy-100 font-mono text-sm"
                        />
                    </div>

                    <div>
                        <div className="text-[11px] uppercase tracking-widest text-gold-600 mb-2">
                            Admin notes (internal)
                        </div>
                        <Textarea
                            data-testid="aff-payout-notes"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={3}
                            className="resize-none bg-white border-navy-100"
                            placeholder="Anything the finance team should note about this payout…"
                        />
                    </div>

                    <div className="rounded-2xl border border-navy-100 bg-white p-4">
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                            <div>
                                <div className="text-[11px] uppercase tracking-widest text-gold-600">
                                    Payment receipt
                                </div>
                                <div className="text-xs text-navy-500 mt-1">
                                    Upload the PDF / screenshot from your bank, Stripe
                                    or PayPal so the affiliate has proof of payment.
                                </div>
                                {row.receipt_url && (
                                    <a
                                        href={row.receipt_url}
                                        target="_blank"
                                        rel="noreferrer"
                                        data-testid="aff-payout-receipt-link"
                                        className="inline-flex items-center gap-1 text-navy-700 mt-2 underline underline-offset-2 text-xs"
                                    >
                                        <FileText size={12} />
                                        {row.receipt_filename || "View current receipt"}
                                    </a>
                                )}
                            </div>
                            <div>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="application/pdf,image/png,image/jpeg,image/webp"
                                    className="hidden"
                                    onChange={(e) => {
                                        const f = e.target.files?.[0];
                                        if (f) uploadReceipt(f);
                                    }}
                                    data-testid="aff-payout-receipt-input"
                                />
                                <Button
                                    variant="outline"
                                    disabled={uploading}
                                    onClick={() => fileInputRef.current?.click()}
                                    data-testid="aff-payout-receipt-upload"
                                    className="rounded-full border-navy-200"
                                >
                                    <Upload size={13} className="mr-1.5" />
                                    {uploading ? "Uploading…" : row.receipt_url ? "Replace receipt" : "Upload receipt"}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>

                <DialogFooter className="gap-2 pt-2 border-t border-navy-100 mt-4 flex-wrap sm:flex-nowrap">
                    <Button
                        variant="outline"
                        disabled={busy || row.status === "on_hold"}
                        onClick={() => persist("on_hold")}
                        data-testid="aff-payout-hold-btn"
                        className="rounded-full border-red-200 text-red-700 hover:bg-red-50"
                    >
                        <AlertCircle size={13} className="mr-1.5" />
                        Place on hold
                    </Button>
                    <Button
                        variant="outline"
                        disabled={busy || row.status === "processing"}
                        onClick={() => persist("processing")}
                        data-testid="aff-payout-processing-btn"
                        className="rounded-full border-navy-200"
                    >
                        <CircleDot size={13} className="mr-1.5" />
                        Move to processing
                    </Button>
                    <Button
                        onClick={() => {
                            if (
                                !window.confirm(
                                    "Mark this payout paid? The affiliate's ledger will flip to Paid immediately."
                                )
                            )
                                return;
                            persist("paid");
                        }}
                        disabled={busy || row.status === "paid"}
                        data-testid="aff-payout-mark-paid-btn"
                        className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
                    >
                        <CheckCircle2 size={13} className="mr-1.5" />
                        {busy === "paid" ? "Saving…" : "Mark paid"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function ReportKpi({ icon: Icon, label, value, blurb, accent }) {
    const accentClasses =
        accent === "emerald"
            ? "text-emerald-700 border-emerald-200 bg-emerald-50"
            : accent === "gold"
            ? "text-gold-700 border-gold-200 bg-gold-50"
            : "text-navy-700 border-navy-100 bg-white";
    return (
        <div className={`rounded-2xl border p-5 ${accentClasses}`}>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest opacity-80">
                <Icon size={13} />
                {label}
            </div>
            <div className="font-display text-2xl mt-1">{value}</div>
            <div className="text-[11px] opacity-70 mt-1">{blurb}</div>
        </div>
    );
}
